'use client';

import { useTranslations } from 'next-intl';

import { useEffect, type JSX } from 'react';

import { useForm } from '~/hooks/useForm';
import {
  Form,
  Stack,
  Button,
  Heading,
  Textarea,
  FormInput,
} from '~/components';

import { useSubmit } from './hooks';
import { contactUsSchema } from './schema';

export function ContactUsForm(): JSX.Element {
  const t = useTranslations();

  const methods = useForm({
    schema: contactUsSchema(t),
    defaultValues: {
      email: '',
      message: '',
      fullName: '',
      phoneNumber: '',
    },
  });
  const { control, formState, setError, reset } = methods;
  const { isValid } = formState;

  const { submit, isPending, isSuccess } = useSubmit({
    setError,
  });

  useEffect(() => {
    if (isSuccess) {
      reset();
    }
  }, [isSuccess, reset]);

  return (
    <Form {...methods} onSubmit={submit} className='flex-1 gap-6'>
      <Heading variant='h3'>{t('models.contactUs.form.title')}</Heading>
      <Stack>
        <FormInput
          required
          name='fullName'
          control={control}
          label={t('models.contactUs.fields.fullName')}
          componentProps={{
            placeholder: t('placeholders.fullName'),
          }}
        />
        <FormInput
          required
          name='email'
          control={control}
          label={t('models.contactUs.fields.email')}
          componentProps={{
            placeholder: t('models.contactUs.placeholders.email'),
          }}
        />
        <FormInput
          control={control}
          name='phoneNumber'
          label={t('models.contactUs.fields.phoneNumber')}
          componentProps={{
            placeholder: t('placeholders.phoneNumber'),
          }}
        />
        <FormInput
          required
          control={control}
          name='message'
          component={Textarea}
          label={t('models.contactUs.fields.description')}
          componentProps={{
            placeholder: t('placeholders.description'),
          }}
        />
        <Button
          type='submit'
          className='uppercase'
          disabled={!isValid || isPending}
        >
          {t('commons.submit')}
        </Button>
      </Stack>
    </Form>
  );
}
